import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import classNames from 'classnames';

import { SUPPORTED_LANGUAGES } from 'src/constants';
import { REACT_APP_API_URL } from 'src/constants/dotenv';

import './chaosWidget.scss';

const { Text } = Typography;
const EVENT_VISIBLE_MS = 2000;
const CLOSE_ANIMATION_MS = 280;
const PREVIEW_EVENT_INTERVAL_MS = 3600;
const LOG_PREFIX = '[chaos-widget]';
const API_BASE_URL = String(REACT_APP_API_URL || '').replace(/\/$/, '');
const API_PREFIX = API_BASE_URL || '/api';
const DEFAULT_WIDGET_COLOR = '#a970ff';

const normalizeWidgetColor = (value) =>
  /^#[0-9a-f]{6}$/i.test(String(value || '')) ? String(value) : DEFAULT_WIDGET_COLOR;

const hexToRgb = (hex) => {
  const normalized = normalizeWidgetColor(hex).slice(1);
  return `${parseInt(normalized.slice(0, 2), 16)}, ${parseInt(normalized.slice(2, 4), 16)}, ${parseInt(normalized.slice(4, 6), 16)}`;
};

const buildApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_PREFIX}${normalizedPath}`;
};

export const ChaosWidgetView = () => {
  const { t, i18n } = useTranslation('chaos_widget');
  const { uuid } = useParams();
  const { pathname, search } = useLocation();
  const mountedRef = useRef(true);
  const streamRef = useRef(null);
  const hideTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const lastEventIdRef = useRef('');
  const popupPhaseRef = useRef('hidden');

  const [widget, setWidget] = useState(null);
  const [activeViewer, setActiveViewer] = useState('');
  const [activeEvent, setActiveEvent] = useState({
    rewardTitle: '',
    rewardImageUrl: '',
    rewardIcon: '✦',
    widgetColor: DEFAULT_WIDGET_COLOR
  });
  const [popupPhase, setPopupPhase] = useState('hidden');
  const [streamVersion, setStreamVersion] = useState(0);
  const isPreview = useMemo(
    () => new URLSearchParams(search).get('preview') === '1',
    [search]
  );

  useEffect(() => {
    popupPhaseRef.current = popupPhase;
  }, [popupPhase]);

  const finalizePopupHide = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setPopupPhase('hidden');
    setActiveViewer('');
  }, []);

  const resolvedUuid = useMemo(() => {
    if (uuid) return uuid;

    const parts = String(pathname || '')
      .split('/')
      .filter(Boolean);

    if (parts.length >= 2 && parts[1] === 'chaos-widget') {
      return parts[0];
    }

    return '';
  }, [pathname, uuid]);

  useEffect(() => {
    console.info(`${LOG_PREFIX} mounted`, {
      href: window.location.href,
      resolvedUuid
    });
  }, [resolvedUuid]);

  useEffect(() => {
    const queryLanguage = new URLSearchParams(search).get('lang');
    const normalized = String(queryLanguage || '')
      .toLowerCase()
      .trim();

    if (!SUPPORTED_LANGUAGES.includes(normalized)) {
      return;
    }

    if (i18n.resolvedLanguage !== normalized && i18n.language !== normalized) {
      i18n.changeLanguage(normalized).catch(() => {});
    }
  }, [i18n, search]);

  const showViewerPopup = useCallback(
    (eventPayload) => {
      const isObjectPayload = eventPayload && typeof eventPayload === 'object';
      const viewerName = String(
        isObjectPayload ? eventPayload.viewerName || '' : eventPayload || ''
      ).trim();
      if (viewerName.toLowerCase() === 'dashboard-test') {
        return;
      }

      const viewer = viewerName || t('overlay.anonymous');
      const nextEvent = {
        rewardTitle: String(
          (isObjectPayload ? eventPayload.rewardTitle : '') || widget?.title || t('overlay.title')
        ).trim(),
        rewardImageUrl: String(isObjectPayload ? eventPayload.rewardImageUrl || '' : '').trim(),
        rewardIcon: String(isObjectPayload ? eventPayload.rewardIcon || '✦' : '✦'),
        widgetColor: normalizeWidgetColor(
          isObjectPayload ? eventPayload.widgetColor || DEFAULT_WIDGET_COLOR : DEFAULT_WIDGET_COLOR
        )
      };
      console.info(`${LOG_PREFIX} popup show`, { viewer, rewardTitle: nextEvent.rewardTitle });

      setActiveViewer(viewer);
      setActiveEvent(nextEvent);

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      // Force reflow-style phase reset so pop-in animation reliably restarts for every new redemption.
      if (popupPhaseRef.current === 'visible') {
        setPopupPhase('hidden');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (mountedRef.current) {
              setPopupPhase('visible');
            }
          });
        });
      } else {
        setPopupPhase('visible');
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }

      hideTimerRef.current = setTimeout(() => {
        if (mountedRef.current) {
          console.info(`${LOG_PREFIX} popup close-start`, { viewer });
          setPopupPhase('closing');
          closeTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
              finalizePopupHide();
            }
          }, CLOSE_ANIMATION_MS);
        }
      }, EVENT_VISIBLE_MS);
    },
    [finalizePopupHide, t, widget?.title]
  );

  const handlePopupAnimationEnd = useCallback(() => {
    if (popupPhase !== 'closing') return;

    console.info(`${LOG_PREFIX} popup hide`, { viewer: activeViewer || '' });
    finalizePopupHide();
  }, [activeViewer, finalizePopupHide, popupPhase]);

  const fetchData = useCallback(async () => {
    if (!resolvedUuid) return;

    try {
      const response = await fetch(buildApiUrl(`/chaos-widget/${resolvedUuid}`));
      if (!response.ok) {
        console.warn(`${LOG_PREFIX} widget fetch not-ok`, {
          status: response.status,
          resolvedUuid
        });
        return;
      }

      const payload = await response.json();
      const data = payload?.data || null;

      if (!mountedRef.current) return;

      setWidget(data?.widget || null);
    } catch (_error) {
      console.error(`${LOG_PREFIX} widget fetch error`, _error);
    }
  }, [resolvedUuid]);

  useEffect(() => {
    if (isPreview) return;
    mountedRef.current = true;
    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, isPreview]);

  useEffect(() => {
    if (isPreview) return () => {};
    if (!resolvedUuid) return () => {};

    const streamUrl = buildApiUrl(`/chaos-widget/${resolvedUuid}/stream`);
    const source = new EventSource(streamUrl);
    streamRef.current = source;

    source.onopen = () => {
      console.info(`${LOG_PREFIX} sse connected`, { streamUrl, resolvedUuid });
    };

    source.addEventListener('chaos_log', (event) => {
      try {
        const parsed = JSON.parse(event.data || '{}');
        console.info(`${LOG_PREFIX} sse chaos_log`, {
          eventId: parsed?._id || '',
          viewerName: parsed?.viewerName || '',
          rewardId: parsed?.rewardId || '',
          status: parsed?.status || ''
        });

        const rewardId = String(parsed?.rewardId || '').trim();
        if (!rewardId) {
          console.info(`${LOG_PREFIX} sse skip event without rewardId`);
          return;
        }

        const eventId = String(parsed?._id || '').trim();
        if (eventId && eventId === lastEventIdRef.current) {
          console.info(`${LOG_PREFIX} sse skip duplicate event`, { eventId });
          return;
        }

        if (eventId) {
          lastEventIdRef.current = eventId;
        }

        showViewerPopup({
          viewerName: parsed?.viewerName || '',
          rewardTitle: parsed?.rewardTitle || '',
          rewardImageUrl: parsed?.rewardImageUrl || '',
          widgetColor: parsed?.widgetColor || DEFAULT_WIDGET_COLOR
        });
      } catch (_error) {
        console.error(`${LOG_PREFIX} sse parse error`, _error);
      }
    });

    source.addEventListener('heartbeat', (event) => {
      try {
        const parsed = JSON.parse(event.data || '{}');
        console.info(`${LOG_PREFIX} sse heartbeat`, parsed);
      } catch (_error) {
        console.info(`${LOG_PREFIX} sse heartbeat`);
      }
    });

    source.addEventListener('connected', (event) => {
      try {
        const parsed = JSON.parse(event.data || '{}');
        console.info(`${LOG_PREFIX} sse connected event`, parsed);
      } catch (_error) {
        console.info(`${LOG_PREFIX} sse connected event`);
      }
    });

    source.onerror = () => {
      console.warn(`${LOG_PREFIX} sse error; reconnect scheduled`, { resolvedUuid });
      source.close();
      streamRef.current = null;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) {
          console.info(`${LOG_PREFIX} sse reconnect attempt`, { resolvedUuid });
          fetchData();
          setStreamVersion((value) => value + 1);
        }
      }, 3000);
    };

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      source.close();
      streamRef.current = null;
    };
  }, [fetchData, isPreview, resolvedUuid, showViewerPopup, streamVersion]);

  useEffect(() => {
    if (!isPreview) return () => {};

    const previewEvents = [
      {
        viewerName: 'pixel_wolf',
        rewardTitle: 'DROP WEAPON',
        rewardIcon: '⌁',
        widgetColor: '#ff4d6d'
      },
      {
        viewerName: 'mira_plays',
        rewardTitle: 'FLASHBANG',
        rewardIcon: '✹',
        widgetColor: '#ffc857'
      },
      {
        viewerName: 'chaos_cat',
        rewardTitle: 'SPIN THE WHEEL',
        rewardIcon: '↻',
        widgetColor: '#38e8ff'
      }
    ];
    let eventIndex = 0;

    const showNextPreviewEvent = () => {
      const event = previewEvents[eventIndex % previewEvents.length];
      eventIndex += 1;
      showViewerPopup(event);
    };

    showNextPreviewEvent();
    const intervalId = window.setInterval(showNextPreviewEvent, PREVIEW_EVENT_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isPreview, showViewerPopup]);

  useEffect(() => {
    document.body.classList.add('chaos-widget-overlay');
    document.documentElement.classList.add('chaos-widget-overlay');

    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.classList.add('chaos-widget-overlay');
    }

    return () => {
      document.body.classList.remove('chaos-widget-overlay');
      document.documentElement.classList.remove('chaos-widget-overlay');

      const cleanupRootEl = document.getElementById('root');
      if (cleanupRootEl) {
        cleanupRootEl.classList.remove('chaos-widget-overlay');
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="chaos-widget-view">
      <div className="chaos-widget-view__stage" aria-live="polite">
        <div
          className={classNames('chaos-widget-view__event', {
            'chaos-widget-view__event--visible': popupPhase === 'visible',
            'chaos-widget-view__event--closing': popupPhase === 'closing',
            'chaos-widget-view__event--hidden': popupPhase === 'hidden'
          })}
          style={{
            '--chaos-accent': activeEvent.widgetColor,
            '--chaos-accent-rgb': hexToRgb(activeEvent.widgetColor)
          }}
          role="status"
          aria-live="assertive"
          onAnimationEnd={handlePopupAnimationEnd}
        >
          <div className="chaos-widget-view__flare" />
          <div className="chaos-widget-view__content">
            <div className="chaos-widget-view__reward-icon" aria-hidden="true">
              {activeEvent.rewardImageUrl ? (
                <img src={activeEvent.rewardImageUrl} alt="" />
              ) : (
                <span>{activeEvent.rewardIcon}</span>
              )}
            </div>
            <div className="chaos-widget-view__copy">
              <div className="chaos-widget-view__viewer">{activeViewer}</div>
              <div className="chaos-widget-view__caption">АКТИВИРУЕТ НАГРАДУ</div>
              <Text className="chaos-widget-view__message" strong>
                {activeEvent.rewardTitle}
              </Text>
            </div>
          </div>
          <div className="chaos-widget-view__progress" />
        </div>
      </div>
    </div>
  );
};
